import { useEffect } from 'react';

import { useAppDispatch, useAppSelector } from '@verantos-dev/core-annotation-tool';
import { useFeatureFlagSplit } from '@verantos-dev/core-utils';

import {
  ANNOTATION_CREATEPROJECT_AGENT_FLAG,
  getCreateProjectWizardSteps,
  type WizardStepKey,
} from '../constants';
import { snapshotIsAgentStepEnabled } from '../state/createProjectSlice';
import { selectActiveStep, selectIsAgentStepEnabled } from '../state/selector';

/**
 * Whether the annotation agent step is part of this wizard session.
 *
 * The release flag is read once per session: the first render stores the live
 * value in the slice and every later render returns that snapshot, so a Split
 * update mid-wizard cannot change which step an index points to. Only
 * `clearAllWizardStates` releases the snapshot.
 */
// TODO: cleanup with cp_annotation_createproject_agent ff
export const useIsAgentStepEnabled = (): boolean => {
  const dispatch = useAppDispatch();
  const snapshot = useAppSelector(selectIsAgentStepEnabled);
  const liveFlag = Boolean(useFeatureFlagSplit(ANNOTATION_CREATEPROJECT_AGENT_FLAG));

  useEffect(() => {
    if (snapshot === null) {
      dispatch(snapshotIsAgentStepEnabled(liveFlag));
    }
  }, [dispatch, snapshot, liveFlag]);

  return snapshot ?? liveFlag;
};

/** Ordered step keys for this wizard session. */
export const useCreateProjectWizardSteps = (): readonly WizardStepKey[] =>
  getCreateProjectWizardSteps(useIsAgentStepEnabled());

/** Whether the annotation agent step is the one on screen. Always false with the flag off. */
// TODO: cleanup with cp_annotation_createproject_agent ff
export const useIsAgentStepActive = (): boolean => {
  const activeStep = useAppSelector(selectActiveStep);
  return useCreateProjectWizardSteps()[activeStep] === 'annotationAgent';
};
